import type { FC } from "react";
import React, { useMemo } from "react";

import cn from "classnames";

import CoreHeaderRightButtonCircleSchevron from "./components/RightButtonCircleSchevron/CoreHeaderRightButtonCircleSchevron";
import { TAG_LEVEL_BY_HEADER_SIZE_MAP } from "./constants/sizes";
import {
    CORE_HEADER_TEST_ID,
    createTestIdForCoreHeader,
} from "./utils/createTestId";
import { extractProps, getChildrenRecord } from "../../../../utils/react";
import { UniCell } from "../../../UniCell";

import type { CoreHeaderProps } from "./types";

import classes from "./CoreHeader.styl";

export const CORE_HEADER_DISPLAY_NAME = "CoreHeader";

const CORE_HEADER_CONTENT_TEST_ID = createTestIdForCoreHeader("content");
const CORE_BUTTON_CIRCLE_SHEVRON_TEST_ID = createTestIdForCoreHeader(
    "button-circle-schevron",
);

/**
 * Core Компонент для отображения и разметки заголовков
 */
export const CoreHeader: FC<CoreHeaderProps> = (props) => {
    const {
        customVarsStyles,
        currentProps: {
            prefix,
            view = "primary",
            size = "21",
            disableRightAddonOffset = false,
            state = "none",
            children,
            width = "100%",
            "data-tsid": tsid = CORE_HEADER_TEST_ID,
            "aria-label": ariaLabel,
            ...restProps
        },
    } = useMemo(() => extractProps(props, ["customVarsStyles"]), [props]);

    const tag = TAG_LEVEL_BY_HEADER_SIZE_MAP[size];

    const {
        LeftAddon,
        Title,
        CounterInline,
        Subtitle,
        RightAddon,
        RightButtonCircle,
        RightButtonInline,
    } = useMemo(
        () =>
            getChildrenRecord(children, prefix, {
                Title: {
                    props: { view, size, tag },
                    required: true,
                },
                CounterInline: {
                    props: { view, size },
                },
                Subtitle: {
                    props: { view, size },
                },
                Unknown: {
                    skipElement: true,
                },
            }),
        [children, prefix, tag, size, view],
    );

    const headerStyles = cn(classes.header, customVarsStyles);

    const headerRightAddonContent =
        RightAddon || RightButtonCircle || RightButtonInline;

    const createRightButtonCircleSchevron = () => {
        return (
            <CoreHeaderRightButtonCircleSchevron
                view={view}
                data-tsid={CORE_BUTTON_CIRCLE_SHEVRON_TEST_ID}
                type="button"
            />
        );
    };

    const coreHeaderRightAddonContent =
        prefix === "HeaderSchevron"
            ? createRightButtonCircleSchevron()
            : headerRightAddonContent;

    return (
        <header
            className={headerStyles}
            style={{ width }}
            data-tsid={tsid}
            aria-label={ariaLabel || Title?.props.children}
        >
            <UniCell
                data-uikit={CORE_HEADER_DISPLAY_NAME}
                /** Добавление обработчиков актуально для HeaderSchevron */
                {...restProps}
                view="custom"
                /** Проставляем custom для отключения оверлей-стилей */
                customColorsStyles=""
                state={state}
                data-tsid={CORE_HEADER_CONTENT_TEST_ID}
                disableRightAddonOffset={disableRightAddonOffset}
                customVarsStyles={classes["custom-styles"]}
            >
                {LeftAddon && <UniCell.Left>{LeftAddon}</UniCell.Left>}

                <UniCell.Middle>
                    <div className={classes["main-text"]}>
                        {Title}
                        {CounterInline}
                    </div>
                </UniCell.Middle>

                {coreHeaderRightAddonContent && (
                    <UniCell.Right>{coreHeaderRightAddonContent}</UniCell.Right>
                )}

                {Subtitle && <UniCell.Bottom>{Subtitle}</UniCell.Bottom>}
            </UniCell>
        </header>
    );
};

CoreHeader.displayName = CORE_HEADER_DISPLAY_NAME;

export default CoreHeader;
